/* Offers: the teaser, the offer card, and the wallet.
 *
 * Modelled on the way Uber shows a promo, which is three surfaces, not one:
 *   1. a slim bar docked above the tab bar — icon, headline, one line of small
 *      print, and a close. It has no claim button: the whole bar is a tap
 *      target, because at that size a second target is a mis-tap waiting to
 *      happen and the shopper has not read the terms yet.
 *   2. the offer's own card, which is where the terms and the claim live. If
 *      several offers are going, they sit side by side and swipe.
 *   3. the wallet on the account page, listing everything available and
 *      everything already taken.
 *
 * The catalogue arrives as JSON from sections/offers.liquid; which of it has
 * been claimed comes from the app proxy. Claiming posts to the proxy, then
 * hands the shopper to Shopify's /discount/<code>, which puts the discount on
 * their session — that route navigates, so the post has to land first.
 */
(function () {
  'use strict';

  var dock = document.querySelector('[data-offer-dock]');
  if (!dock) return;

  /* Filled from the app proxy. Empty until it answers, and empty for good if
   * it does not: an offer we cannot confirm is live is not one to show. */
  var catalogue = [];

  var endpoint = dock.getAttribute('data-endpoint') || '/apps/tms/offers';
  var SEEN_KEY = 'tms:offers:dismissed';
  var WALLET_KEY = 'tms:offers:wallet';
  var UNLOCKED_KEY = 'tms:offers:unlocked';
  var BROWSER_KEY = 'tms:browser';
  /* Codes typed into the promotions page's box (MAGIC10 and the like). They
   * are not in the proxy's catalogue, so without their own list a saved code
   * went onto the bag and then appeared nowhere (Rich, 2026-09-28). */
  var CODES_KEY = 'tms:offers:codes';
  /* What each typed code does, in Shopify's own words (the proxy's
   * /offers/code answers with the discount's summary). */
  var CODEMETA_KEY = 'tms:offers:codemeta';
  function readMeta() {
    try { return JSON.parse(localStorage.getItem(CODEMETA_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function writeMeta(code, summary) {
    var meta = readMeta();
    meta[code] = summary;
    try { localStorage.setItem(CODEMETA_KEY, JSON.stringify(meta)); } catch (e) {}
  }
  /* "10% off entire order • One use per customer" reads as
   * "10% off entire order · 1 time use" (Rich, 2026-09-28). */
  function plainSummary(text) {
    return String(text || '')
      .replace(/One use per customer/g, '1 time use')
      .replace(/\s*•\s*/g, ' · ');
  }

  function label(name, fallback) {
    return dock.getAttribute('data-' + name + '-label') || fallback;
  }

  function browserId() {
    var id = '';
    try { id = localStorage.getItem(BROWSER_KEY) || ''; } catch (e) {}
    if (!id) {
      id = 'b' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      try { localStorage.setItem(BROWSER_KEY, id); } catch (e) {}
    }
    return id;
  }
  function readSet(key) {
    try { return JSON.parse(localStorage.getItem(key) || '[]') || []; }
    catch (e) { return []; }
  }
  function addTo(key, id) {
    var list = readSet(key);
    if (list.indexOf(id) < 0) list.push(id);
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) {}
  }
  function esc(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  var fromLink = '';
  try { fromLink = new URLSearchParams(location.search).get('offer') || ''; }
  catch (e) {}
  if (fromLink) addTo(UNLOCKED_KEY, fromLink);

  /* No filtering here any more. The proxy decides what this shopper may see —
   * tags in particular are read from Shopify, because anything this page could
   * send is something a shopper could edit. Link-only ids are sent up so the
   * server can include them; remembering them locally is what keeps the offer
   * alive after they navigate off the Klaviyo landing URL. */
  var claimed = readSet(WALLET_KEY);
  var dismissed = readSet(SEEN_KEY);
  function isClaimed(id) { return claimed.indexOf(id) >= 0; }
  function mine() { return catalogue; }

  /* The default offer mark is a gold coin, drawn here.
   *
   * Not an emoji: an emoji is someone else's artwork and a different drawing
   * on every platform — Apple's 💵 is a photographic bundle of notes, Android's
   * is a flat green rectangle — so the store cannot know what it is shipping.
   * The same call was already made for the poker card face; see base.css.
   *
   * Drawn rather than shipped as a file so it stays crisp from 34px in the bar
   * to 116px on the card, and it carries literal gold rather than theme
   * tokens, because a coin is gold on a white page and on a black one.
   *
   * Gradient ids have to be unique per instance: the bar, the card and the
   * wallet can all be in the document at once, and duplicate ids would send
   * every coin to whichever gradient the browser found first. */
  var coinSeq = 0;

  function coin() {
    var n = 'oc' + (++coinSeq);
    return '<svg class="offercoin" viewBox="0 0 64 64" aria-hidden="true" focusable="false">' +
      '<defs>' +
        '<linearGradient id="' + n + 'r" x1=".18" y1="0" x2=".82" y2="1">' +
          '<stop offset="0" stop-color="#FCE690"/>' +
          '<stop offset=".48" stop-color="#E0AE36"/>' +
          '<stop offset="1" stop-color="#9E6C09"/>' +
        '</linearGradient>' +
        '<linearGradient id="' + n + 'f" x1=".2" y1="0" x2=".8" y2="1">' +
          '<stop offset="0" stop-color="#F8D869"/>' +
          '<stop offset="1" stop-color="#C08C18"/>' +
        '</linearGradient>' +
      '</defs>' +
      /* Rim, then a recessed face, then one highlight arc. Three shapes is
       * all that survives being 34px wide; milled edges turn to mud. */
      '<circle cx="32" cy="32" r="31" fill="url(#' + n + 'r)"/>' +
      '<circle cx="32" cy="32" r="24" fill="url(#' + n + 'f)"' +
        ' stroke="#8A5E06" stroke-opacity=".38" stroke-width="1.5"/>' +
      '<path d="M18.5 24.5A16.5 16.5 0 0 1 30 15.5" fill="none"' +
        ' stroke="#FFF6D2" stroke-opacity=".55" stroke-width="3.2"' +
        ' stroke-linecap="round"/>' +
    '</svg>';
  }

  /* The store's gold monogram coin, set in the theme editor. */
  var coinUrl = (dock.getAttribute('data-coin-url') || '').trim();

  function art(offer, size) {
    /* No loading="lazy" on either of these. showTeaser writes the bar into
     * the dock while it is still hidden, and an image parsed inside a
     * display:none subtree never schedules its fetch — the coin simply never
     * appeared, with no error to show for it. They are drawn the moment they
     * are inserted anyway, so lazy buys nothing. */
    if (offer.art) {
      return '<span class="offerart offerart--' + size + ' offerart--' +
        (offer.tint || 'mint') + '"><img src="' + esc(offer.art) +
        '" alt="" decoding="async"></span>';
    }
    /* The coin is the whole mark, so it brings no tint behind it. The image
     * is positioned by base.css rather than object-fit: its canvas is
     * 1500x1920 with the coin occupying only the middle 62% x 56%, so
     * `contain` would draw it at just over half the size of the slot. */
    if (coinUrl) {
      return '<span class="offerart offerart--' + size + ' offerart--coin">' +
        '<img class="offercoin offercoin--img" src="' + esc(coinUrl) +
        '" alt="" decoding="async"></span>';
    }
    return '<span class="offerart offerart--' + size + ' offerart--coin">' +
      coin() + '</span>';
  }

  /* ---- 1. the teaser ---- */

  function showTeaser(offer) {
    var taken = isClaimed(offer.id);
    /* The bar states what it is and offers the one action that fits. Claimed,
     * it stops selling and starts reminding — an offer they already hold is
     * money waiting, and the useful thing is a way to spend it. */
    var action = taken
      ? '<a class="offerbar__go" href="/discount/' +
          encodeURIComponent(offer.code) + '?redirect=' +
          encodeURIComponent(shopPath()) + '">' +
          esc(label('use', 'Use it')) + '</a>'
      : '<button class="offerbar__go offerbar__go--new" type="button" ' +
          'data-claim>' + esc(label('claim', 'Claim')) + '</button>';

    dock.innerHTML =
      '<div class="offerbar">' +
        '<button class="offerbar__open" type="button" data-open>' +
          art(offer, 'sm') +
          '<span class="offerbar__words">' +
            '<span class="offerbar__head">' + esc(offer.title) + '</span>' +
            '<span class="offerbar__sub">' +
              (taken ? esc(label('have', 'You have this code available'))
                     : esc(offer.subtitle || '')) +
            '</span>' +
          '</span>' +
        '</button>' +
        action +
        '<button class="offerbar__x" type="button" data-dismiss aria-label="' +
          esc(label('close', 'Dismiss')) + '">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" ' +
          'stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</div>';
    dock.hidden = false;
    liftDock();
    requestAnimationFrame(function () { dock.classList.add('is-in'); });
    reportSeen([offer.id]);

    dock.querySelector('[data-dismiss]').addEventListener('click', function () {
      addTo(SEEN_KEY, offer.id);
      hideTeaser();
    });
    dock.querySelector('[data-open]').addEventListener('click', function () {
      openSheet(offer.id);
    });
    var claimButton = dock.querySelector('[data-claim]');
    if (claimButton) {
      claimButton.addEventListener('click', function (event) {
        event.stopPropagation();
        claim(offer, claimButton);
      });
    }
  }

  /* The dock's CSS height clears the phone tab bar, but a product page adds
   * the buy card above that bar (sections/main-product.liquid [data-buybar]),
   * and the teaser landed on top of its price and button. When the card is
   * showing, sit 10px above its top edge instead; it can appear, disappear
   * and grow (its details panel), so it is watched rather than measured once. */
  var buybar = document.querySelector('[data-buybar]');
  function liftDock() {
    if (!buybar) return;
    var shown = !buybar.hidden && buybar.getClientRects().length > 0;   // offsetParent is always null for position:fixed
    dock.style.bottom = shown
      ? Math.max(0, window.innerHeight - buybar.getBoundingClientRect().top + 10) + 'px'
      : '';
  }
  if (buybar) {
    if ('MutationObserver' in window) {
      new MutationObserver(liftDock).observe(buybar, { attributes: true, attributeFilter: ['hidden'] });
    }
    if ('ResizeObserver' in window) new ResizeObserver(liftDock).observe(buybar);
    window.addEventListener('resize', liftDock);
  }

  function hideTeaser() {
    dock.classList.remove('is-in');
    setTimeout(function () { dock.hidden = true; dock.innerHTML = ''; }, 260);
  }

  /* ---- 2. the offer's own card ---- */

  var sheet = null;
  /* What had focus before the sheet opened, so closing can hand it back.
   * showModal() would have done this; show() does not. */
  var lastFocus = null;
  /* The teaser docks at z-index 44 and the sheet sits at 41, so with the card
   * open the bar would float over its own scrim. showModal() used to hide
   * that, because the top layer is above every z-index on the page. The bar
   * is redundant while the card is up anyway — same offer, larger — so it is
   * hidden on open and put back on close, which keeps the reminder alive for
   * a shopper who reads the terms and decides later. */
  var teaserOffer = null;
  /* Guards teardown against running twice — once from closeSheet and again if
   * a `close` event does arrive. Starts true: nothing is open yet. */
  var tornDown = true;

  /* Undo everything opening the card did. Idempotent, and never relies on the
   * dialog telling us it closed. */
  function teardown() {
    if (tornDown) return;
    tornDown = true;
    document.documentElement.style.overflow = '';
    if (teaserOffer && dismissed.indexOf(teaserOffer.id) < 0) {
      showTeaser(teaserOffer);
    }
    teaserOffer = null;
    /* Hand focus back to whatever opened the card. showTeaser rebuilds the
     * bar, so the element that was clicked no longer exists — prefer its
     * replacement, and fall back to wherever focus was. Without this it lands
     * on the body and the next Tab restarts at the top of the page. */
    var back = dock.hidden ? null : dock.querySelector('[data-open]');
    if (!back && lastFocus && lastFocus.focus &&
        document.contains(lastFocus)) back = lastFocus;
    if (back && back.focus) back.focus();
    lastFocus = null;
  }

  function closeSheet() {
    if (sheet && sheet.open) sheet.close();
    teardown();
  }

  /* How much room the navigation needs at each edge, right now.
   *
   * Both are measured rather than assumed. The header is sticky and sits below
   * the announcement bar until the page scrolls, so its bottom edge moves; the
   * tab bar exists only below 768px. An element that is off screen or hidden
   * asks for nothing. 24px of air matches what the teaser already leaves. */
  var NAV_AIR = 24;

  function navClearTop() {
    var head = document.querySelector('.navdock');
    if (!head) return 0;
    var box = head.getBoundingClientRect();
    if (!box.height || box.bottom <= 0) return 0;
    /* Only a bar resting at the top of the window is navigation to clear —
     * one scrolled halfway down the page is just content. */
    if (box.top > window.innerHeight * 0.5) return 0;
    return Math.max(0, Math.round(box.bottom));
  }

  function navClearBottom() {
    var tab = document.querySelector('.tabbar');
    if (!tab || getComputedStyle(tab).display === 'none') return 0;
    var box = tab.getBoundingClientRect();
    if (!box.height) return 0;
    return Math.max(0, Math.round(window.innerHeight - box.top + NAV_AIR));
  }

  function cardHtml(offer) {
    var taken = isClaimed(offer.id);
    return '<article class="offersheet__card" data-card="' + esc(offer.id) + '">' +
      /* The close sits ON the card, not out on the scrim. Floating it at the
       * dialog's top-right put it a long way from the thing it closes — and
       * with the sheet now inset below the header, next to the page's own
       * chrome. An X rather than a chevron: the card is dismissed, not
       * collapsed into anything. */
      '<button class="offersheet__x" type="button" data-close aria-label="' +
        esc(label('sheet-close', 'Close')) + '">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" ' +
        'stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
      '</button>' +
      art(offer, 'lg') +
      '<h2 class="offersheet__head">' + esc(offer.title) + '</h2>' +
      (offer.terms
        ? '<div class="offersheet__terms rte">' + offer.terms + '</div>'
        : (offer.subtitle
            ? '<p class="offersheet__terms">' + esc(offer.subtitle) + '</p>'
            : '')) +
      (offer.expires
        ? '<p class="offersheet__ends">' + esc(label('ends', 'ends')) + ' ' +
          esc(offer.expires) + '</p>'
        : '') +
      '<hr class="offersheet__rule">' +
      (taken
        ? '<a class="btn offersheet__go" href="/discount/' +
            encodeURIComponent(offer.code) + '?redirect=%2Fcart">' +
            esc(label('use', 'Use it')) + '</a>'
        : '<button class="btn offersheet__go" type="button" data-claim="' +
            esc(offer.id) + '">' +
            esc(label('sheet-claim', 'Claim offer')) + '</button>') +
    '</article>';
  }

  function openSheet(startId) {
    var list = mine();
    if (!list.length) return;
    if (!sheet) {
      sheet = document.createElement('dialog');
      sheet.className = 'offersheet';
      sheet.setAttribute('aria-label', label('sheet-title', 'Your offers'));
      document.body.appendChild(sheet);
      /* The dialog box is the scrim, so a click that lands on the dialog
       * itself rather than a card is a click outside the card. */
      sheet.addEventListener('click', function (event) {
        if (event.target === sheet) closeSheet();
      });
      /* `show()` rather than `showModal()`, so the tab bar stays above the
       * scrim and usable — see the .offersheet comment in base.css. The price
       * is that Esc, the scroll lock and focus are ours to handle. */
      document.addEventListener('keydown', function (event) {
        if (sheet.open && (event.key === 'Escape' || event.key === 'Esc')) {
          event.preventDefault();
          closeSheet();
        }
      });
      /* Only a safety net. The teardown is driven by closeSheet(), because the
       * `close` event cannot be relied on: measured on the hosted draft, a
       * dialog closed with .close() fired no `close` event at all — for
       * show() and showModal() alike — which left the page scroll-locked with
       * the card already gone. Every close path here is ours, so the teardown
       * is called directly and this listener only catches a native close. */
      sheet.addEventListener('close', teardown);
    }
    sheet.innerHTML =
      '<div class="offersheet__track" data-track>' +
        list.map(cardHtml).join('') +
      '</div>' +
      (list.length > 1
        ? '<div class="offersheet__dots" data-dots>' +
            list.map(function (_, i) {
              return '<span class="offersheet__dot' + (i ? '' : ' is-on') + '"></span>';
            }).join('') +
          '</div>'
        : '');

    /* One close per card now, so every card carries its own. */
    Array.prototype.forEach.call(sheet.querySelectorAll('[data-close]'),
      function (button) {
        button.addEventListener('click', function () { closeSheet(); });
      });
    Array.prototype.forEach.call(sheet.querySelectorAll('[data-claim]'),
      function (button) {
        button.addEventListener('click', function () {
          var offer = list.filter(function (o) {
            return o.id === button.getAttribute('data-claim');
          })[0];
          if (offer) claim(offer, button);
        });
      });

    var track = sheet.querySelector('[data-track]');
    var dots = sheet.querySelectorAll('.offersheet__dot');
    if (dots.length) {
      track.addEventListener('scroll', function () {
        var i = Math.round(track.scrollLeft / track.clientWidth);
        Array.prototype.forEach.call(dots, function (dot, n) {
          dot.classList.toggle('is-on', n === i);
        });
      }, { passive: true });
    }

    /* Remember the bar so close() can put it back, then get it out from over
     * the scrim. dock.hidden is the honest test of whether it is up. */
    teaserOffer = dock.hidden ? null
      : list.filter(function (o) { return o.id === startId; })[0] || null;
    if (!dock.hidden) hideTeaser();

    /* The clearances in base.css are static defaults, and static is not good
     * enough: the header is sticky and the announcement bar pushes it down, so
     * with the bar showing its real bottom edge is 90px, not the 56px --navh
     * implies — the sheet covered the last 34px of the nav. Both edges are
     * therefore measured at open time, which is safe because the scroll is
     * locked a line later and nothing moves afterwards. */
    sheet.style.setProperty('--offer-clear-t', navClearTop() + 'px');
    sheet.style.setProperty('--offer-clear-b', navClearBottom() + 'px');

    lastFocus = document.activeElement;
    tornDown = false;
    sheet.show();
    document.documentElement.style.overflow = 'hidden';

    var index = 0;
    list.forEach(function (offer, i) { if (offer.id === startId) index = i; });
    if (index) track.scrollLeft = track.clientWidth * index;

    /* Focus the card that is actually on screen, not the first in the track —
     * opening on offer three and focusing offer one scrolls it back. */
    var opened = sheet.querySelectorAll('.offersheet__card')[index];
    var target = (opened && opened.querySelector('.offersheet__go')) ||
      sheet.querySelector('[data-close]');
    if (target && target.focus) target.focus({ preventScroll: true });
  }

  /* ---- claiming ---- */

  function claim(offer, button) {
    var before = button.textContent;
    button.disabled = true;
    button.textContent = label('claiming', 'Adding…');

    var go = function () {
      remember(offer.code);
      location.href = '/discount/' + encodeURIComponent(offer.code) +
        '?redirect=' + encodeURIComponent(location.pathname + location.search);
    };
    var added = function () {
      button.textContent = label('claimed', 'Added to your offers');
      addTo(WALLET_KEY, offer.id);
      if (claimed.indexOf(offer.id) < 0) claimed.push(offer.id);
      go();
    };
    /* The proxy refused (it answers 4xx with an error now, QA 2026-09-29):
     * say so and leave the offer unclaimed, rather than "Added" over a
     * failure. */
    var refused = function (why) {
      button.disabled = false;
      button.textContent = before;
      notice(why || label('claim-failed', 'That offer could not be added. Try again in a moment.'), false);
    };
    try {
      fetch(endpoint + '/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        /* No code sent: the server reads it from the catalogue. If the code
         * came from here, anyone could post any string and have it recorded. */
        body: JSON.stringify({ id: offer.id, browser: browserId() })
      }).then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          var err = body && (body.error || body.message);
          /* 409 {ok:true, already:true} means it was claimed before: that is
           * a success, not a refusal. */
          if ((r.ok && !(body && body.error)) || (body && body.already)) { added(); return; }
          /* 5xx, or no readable answer: a proxy that is down must not cost
           * the shopper their discount, so the code still goes on the
           * session; it just is not called "added". */
          if (r.status >= 500 || !body) { go(); return; }
          refused(typeof err === 'string' ? err : '');
        });
      }, go);
    } catch (e) { go(); }
  }

  /* Let a claimed offer go: ask the proxy to drop it, forget it here, and take
   * its code off the bag if it is on it. A proxy without the route yet (404)
   * is not a failure: the local copy is all there is to remove. */
  function unclaim(offer, button) {
    if (button) button.disabled = true;
    var forget = function () {
      claimed = claimed.filter(function (id) { return id !== offer.id; });
      try { localStorage.setItem(WALLET_KEY, JSON.stringify(claimed)); } catch (e) {}
      dropCartCode(offer.code);
      renderWallet();
    };
    var failed = function () {
      if (button) button.disabled = false;
      notice(label('unclaim-failed', 'That offer could not be removed. Try again in a moment.'), false);
    };
    try {
      fetch(endpoint + '/unclaim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: offer.id, browser: browserId() })
      }).then(function (r) {
        if (r.ok || r.status === 404) forget(); else failed();
      }, failed);
    } catch (e) { forget(); }
  }

  /* Take one code off the bag, keeping the others. Shopify replaces the
   * whole list on /cart/update.js, so send what should remain. */
  function dropCartCode(code) {
    if (!code) return Promise.resolve();
    var c = String(code).toUpperCase();
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var codes = (cart.discount_codes || []).map(function (d) { return d.code; });
        if (!codes.some(function (x) { return String(x).toUpperCase() === c; })) return;
        var keep = codes.filter(function (x) { return String(x).toUpperCase() !== c; });
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: keep.join(',') })
        }).then(function () {
          try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
        });
      }).catch(function () {});
  }

  /* ---- 3. the wallet ---- */

  function catalogueHas(code) {
    var c = String(code || '').toUpperCase();
    return mine().some(function (o) { return String(o.code || '').toUpperCase() === c; });
  }

  /* A typed code gets a plain row: the code itself, what it is, one way to
   * use it and one way to let it go. */
  var askedMeta = false;
  function codeRows() {
    var meta = readMeta();
    var codes = readSet(CODES_KEY).filter(function (c) { return !catalogueHas(c); });
    /* Codes saved before the proxy sent a summary, or while it could not:
     * ask once per page view and draw the rows again with the answer. */
    var missing = codes.filter(function (c) { return !meta[c]; });
    if (missing.length && !askedMeta) {
      askedMeta = true;
      Promise.all(missing.map(function (c) {
        return fetch(endpoint + '/code?code=' + encodeURIComponent(c) +
                     '&browser=' + encodeURIComponent(browserId()),
                     { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (a) { if (a && a.summary) writeMeta(c, a.summary); })
          .catch(function () {});
      })).then(function () { if (Object.keys(readMeta()).length) renderWallet(); });
    }
    return codes
      .map(function (code) {
        var what = meta[code]
          ? plainSummary(meta[code])
          : label('code_saved', 'Your code · goes on at checkout');
        return '<div class="offertile is-claimed">' +
            art({ id: 'code-' + code, title: code }, 'md') +
            '<span class="offertile__words">' +
              '<span class="offertile__head">' + esc(code) + '</span>' +
              '<span class="offertile__sub">' +
                esc(what) +
                '<button class="offertile__info" type="button" data-forget="' + esc(code) +
                  '" aria-label="' + esc(label('forget', 'Remove')) + ' ' + esc(code) + '">' +
                  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17" ' +
                  'stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
                '</button>' +
              '</span>' +
            '</span>' +
            '<a class="offertile__go" href="/discount/' + encodeURIComponent(code) +
              '?redirect=' + encodeURIComponent(shopPath()) + '">' +
              esc(label('shop', 'Shop')) + '</a>' +
          '</div>';
      }).join('');
  }

  function renderWallet() {
    var host = document.querySelector('[data-offers-wallet]');
    if (!host) return;
    var list = mine();
    var typed = codeRows();
    if (!list.length && !typed) {
      host.innerHTML = '<p class="muted cap">' +
        esc(label('empty', 'No offers right now.')) + '</p>';
      return;
    }
    /* The action sits on the row, not behind a tap: a shopper on this page has
     * come to spend something, and an offer they already hold should take them
     * shopping in one move. The terms move to the info button, which opens the
     * same card the pop-up opens. */
    host.innerHTML = list.map(function (offer) {
      var taken = isClaimed(offer.id);
      return '<div class="offertile' + (taken ? ' is-claimed' : '') + '">' +
          art(offer, 'md') +
          '<span class="offertile__words">' +
            '<span class="offertile__head">' + esc(offer.title) + '</span>' +
            '<span class="offertile__sub">' +
              (offer.expires
                ? esc(label('ends', 'ends')) + ' ' + esc(offer.expires) +
                  (offer.subtitle ? ' &middot; ' : '')
                : '') +
              esc(offer.subtitle || '') +
              '<button class="offertile__info" type="button" data-open="' +
                esc(offer.id) + '" aria-label="' +
                esc(label('terms', 'See offer')) + '">' +
                '<svg viewBox="0 0 24 24" aria-hidden="true">' +
                '<circle cx="12" cy="12" r="10" fill="currentColor"/>' +
                '<path d="M12 10.6v6M12 7.4v.1" stroke="var(--bg)" stroke-width="2" ' +
                'stroke-linecap="round" fill="none"/></svg>' +
              '</button>' +
              (taken
                ? '<button class="offertile__info" type="button" data-unclaim="' +
                    esc(offer.id) + '" aria-label="' + esc(label('forget', 'Remove')) +
                    ' ' + esc(offer.title) + '">' +
                    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17" ' +
                    'stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
                  '</button>'
                : '') +
            '</span>' +
          '</span>' +
          (taken
            ? '<a class="offertile__go" href="/discount/' +
                encodeURIComponent(offer.code) + '?redirect=' +
                encodeURIComponent(shopPath()) + '">' +
                esc(label('shop', 'Shop')) + '</a>'
            : '<button class="offertile__go offertile__go--new" type="button" ' +
                'data-claim="' + esc(offer.id) + '">' +
                esc(label('claim', 'Claim')) + '</button>') +
        '</div>';
    }).join('') + typed;

    if (list.length) reportSeen(list.map(function (offer) { return offer.id; }));

    Array.prototype.forEach.call(host.querySelectorAll('[data-forget]'),
      function (button) {
        button.addEventListener('click', function () {
          var gone = button.getAttribute('data-forget');
          var keep = readSet(CODES_KEY).filter(function (c) { return c !== gone; });
          try { localStorage.setItem(CODES_KEY, JSON.stringify(keep)); } catch (e) {}
          dropCartCode(gone);
          renderWallet();
        });
      });

    Array.prototype.forEach.call(host.querySelectorAll('[data-unclaim]'),
      function (button) {
        button.addEventListener('click', function () {
          var offer = list.filter(function (o) {
            return o.id === button.getAttribute('data-unclaim');
          })[0];
          if (offer) unclaim(offer, button);
        });
      });

    Array.prototype.forEach.call(host.querySelectorAll('[data-open]'),
      function (button) {
        button.addEventListener('click', function () {
          openSheet(button.getAttribute('data-open'));
        });
      });
    Array.prototype.forEach.call(host.querySelectorAll('[data-claim]'),
      function (button) {
        button.addEventListener('click', function () {
          var offer = list.filter(function (o) {
            return o.id === button.getAttribute('data-claim');
          })[0];
          if (offer) claim(offer, button);
        });
      });
  }

  /* Where "Shop" sends someone once the code is on their session. Their bag if
   * they have one started, the shop if they do not — landing on an empty cart
   * with a discount attached is a dead end. */
  function shopPath() {
    var count = 0;
    try {
      var el = document.querySelector('[data-cart-count]');
      count = el ? parseInt(el.textContent, 10) || 0 : 0;
    } catch (e) {}
    return count > 0 ? '/cart' : '/collections/all';
  }

  /* The code field on the promotions page. Shopify's own route does the work,
   * so a typed code and a claimed one take exactly the same path. */
  function wireCodeForm() {
    var form = document.querySelector('[data-promo-form]');
    if (!form) return;
    var field = form.querySelector('input[name="code"]');
    var button = form.querySelector('button[type="submit"]');
    var error = document.querySelector('[data-promo-error]');

    function fail(text) {
      if (!error) { notice(text, false); return; }   /* no slot: fall back */
      error.textContent = text;
      error.hidden = false;
      field.setAttribute('aria-invalid', 'true');
      field.select();
    }

    function clearError() {
      if (error) { error.hidden = true; error.textContent = ''; }
      field.removeAttribute('aria-invalid');
    }

    /* Clear as soon as they start correcting it, so the message never
     * contradicts what is in the box. */
    field.addEventListener('input', clearError);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var value = (field.value || '').trim();
      if (!value) return;

      /* Ask whether the code exists before going anywhere. Shopify's
       * /discount/ route accepts anything, so without this a typo costs a page
       * load and changes nothing visible — and with an empty bag it would even
       * be reported as saved. A wrong code should fail here, immediately,
       * without the page moving. */
      clearError();
      if (button) button.disabled = true;
      fetch(endpoint + '/code?code=' + encodeURIComponent(value) +
            '&browser=' + encodeURIComponent(browserId()),
            { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (r.status === 429) return { limited: true };
          return r.ok ? r.json() : null;
        })
        .catch(function () { return null; })
        .then(function (answer) {
          if (button) button.disabled = false;

          /* Rate limited: the code could not be checked, and sending an
           * unchecked code to /discount/ is what the check exists to stop. */
          if (answer && answer.limited) {
            fail(label('limited', 'Too many codes tried. Wait a few minutes and try again.'));
            return;
          }

          /* known:false means we could not reach Shopify to ask. Let it
           * through rather than calling a good code invalid — the worst case
           * is the old behaviour, which is better than a false accusation. */
          if (answer && answer.known && !answer.exists) {
            fail(label('invalid', "[code] isn't a valid code.")
                   .replace('[code]', value));
            return;
          }
          if (answer && answer.known && answer.status === 'EXPIRED') {
            fail(label('expired', '[code] has expired.').replace('[code]', value));
            return;
          }
          remember(value);
          if (!catalogueHas(value)) {
            addTo(CODES_KEY, value.toUpperCase());
            if (answer && answer.summary) writeMeta(value.toUpperCase(), answer.summary);
          }
          location.href = '/discount/' + encodeURIComponent(value) +
            '?redirect=' + encodeURIComponent(location.pathname);
        });
    });
  }

  /* Tell the backend which offers were actually put in front of them. Reach is
   * counted server-side; this is the narrower number — the card was drawn, not
   * merely delivered. Fire-and-forget: a failed beacon must never cost the
   * shopper anything. */
  var reported = false;
  function reportSeen(ids) {
    if (reported || !ids.length) return;
    reported = true;
    try {
      fetch(endpoint + '/seen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: ids, browser: browserId() }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  /* Shopify's /discount/ route accepts any string, attaches it to the session,
   * marks it inapplicable if it is wrong, and says nothing. Remember what was
   * attempted, then read the cart back and say which it was. */
  var TRIED_KEY = 'tms:offers:tried';

  function remember(code) {
    try { sessionStorage.setItem(TRIED_KEY, code); } catch (e) {}
  }

  function notice(text, good) {
    var bar = document.createElement('div');
    bar.className = 'offernote' + (good ? ' is-good' : ' is-bad');
    bar.setAttribute('role', 'status');
    bar.textContent = text;
    document.body.appendChild(bar);
    requestAnimationFrame(function () { bar.classList.add('is-in'); });
    setTimeout(function () {
      bar.classList.remove('is-in');
      setTimeout(function () { bar.remove(); }, 300);
    }, 6000);
  }

  function reportOutcome() {
    var code = '';
    try {
      code = sessionStorage.getItem(TRIED_KEY) || '';
      sessionStorage.removeItem(TRIED_KEY);
    } catch (e) {}
    if (!code) return;

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;

        /* An empty bag makes Shopify mark EVERY code inapplicable, valid ones
         * included — there is nothing to evaluate it against. Since most
         * shoppers claim before they have added anything, reporting failure
         * here would tell nearly everyone their working offer was broken. */
        if (!cart.item_count) {
          notice(label('saved', '[code] is saved. It goes on at checkout.')
                   .replace('[code]', code), true);
          return;
        }

        var match = (cart.discount_codes || []).filter(function (d) {
          return (d.code || '').toUpperCase() === code.toUpperCase();
        })[0];
        var template = match && match.applicable
          ? label('applied', '[code] is applied to your bag.')
          : label('rejected', '[code] could not be applied. Check the ' +
                              'minimum spend, or whether it has expired.');
        notice(template.replace('[code]', code), !!(match && match.applicable));
        /* A code that does not fit the bag comes straight back off it, so
         * tries do not pile up until checkout refuses "too many codes". */
        if (match && match.applicable === false) dropCartCode(match.code);
      }).catch(function () {});
  }

  /* Shop / Use it are plain links to the same /discount/ route, so they need
   * the same confirmation as Claim. One delegated listener covers the teaser,
   * the wallet and the offer card, reading the code out of the href so there
   * is nothing to keep in step. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href^="/discount/"]');
    if (!link) return;
    var code = decodeURIComponent(
      (link.getAttribute('href') || '').split('/discount/')[1].split('?')[0] || '');
    if (code) remember(code);
  }, true);

  /* ---- start ---- */

  function decide() {
    renderWallet();
    wireCodeForm();
    reportOutcome();
    /* Claimed offers stay in the pool now: the bar reminds them they are
     * holding something. Dismissal is the control — close it and it is gone
     * for that offer, claimed or not. */
    var pool = mine().filter(function (offer) {
      return offer.popup && dismissed.indexOf(offer.id) < 0;
    });
    var unclaimed = pool.filter(function (o) { return !isClaimed(o.id); });
    if (unclaimed.length) pool = unclaimed;   // something to act on wins
    if (!pool.length) return;
    /* An offer arrived at by link jumps the queue: they clicked through for it. */
    var pick = pool.filter(function (o) { return o.id === fromLink; })[0] || pool[0];
    if (fromLink) { whenClear(function () { showTeaser(pick); }, 400); return; }
    /* With Ask Pip on, Pip mentions the offer in the chat instead; only an
     * offer someone clicked through for still gets the bar. */
    if (dock.hasAttribute('data-quiet')) return;
    afterLady(function () { whenClear(function () { showTeaser(pick); }, 1400); });
  }

  /* One pop-up at a time (Rich, 2026-09-29): cookie banner, then Find the
   * Lady, then this. Shopify's banner can be injected after we decide, so
   * the teaser waits a moment and then until neither the banner nor the
   * game (nor another dialog) is on screen. */
  function popupUp() {
    var consent = document.getElementById('shopify-pc__banner');
    if (consent && consent.offsetHeight > 0) return true;
    /* Rich, 10-08: cookie banner, then the CAD/USD question (snippets/cad-prompt), then the rest. */
    if (/^(pending|open)$/.test(document.documentElement.getAttribute('data-cadp') || '')) return true;
    var ftl = document.getElementById('ftl');
    if (ftl && !ftl.hidden) return true;
    return !!document.querySelector('dialog[open]');
  }
  function whenClear(go, wait) {
    setTimeout(function check() {
      if (popupUp()) { setTimeout(check, 1000); return; }
      go();
    }, Math.max(wait || 0, 2000));
  }

  /* Find the Lady is the first thing a new visitor sees (Rich, 2026-09-28):
   * the teaser waits until the game has closed, or has said it will not open
   * on this page (assets/find-the-lady.js sets html[data-ftl]). If the game
   * never reports — its script failed — the teaser goes ahead after 4s
   * rather than never. */
  function afterLady(go) {
    if (!document.getElementById('ftl')) { go(); return; }
    var html = document.documentElement;
    var state = html.getAttribute('data-ftl');
    if (state === 'done' || state === 'off') { go(); return; }
    var fired = false;
    function once() { if (!fired) { fired = true; go(); } }
    function onState(e) {
      if (e.detail === 'done' || e.detail === 'off') {
        document.removeEventListener('ftl:state', onState);
        once();
      }
    }
    document.addEventListener('ftl:state', onState);
    if (!state) {
      setTimeout(function () {
        if (!html.getAttribute('data-ftl')) { document.removeEventListener('ftl:state', onState); once(); }
      }, 4000);
    }
  }

  var query = '?browser=' + encodeURIComponent(browserId()) +
    '&unlocked=' + encodeURIComponent(readSet(UNLOCKED_KEY).join(','));

  fetch(endpoint + query, { headers: { 'Accept': 'application/json' } })
    .then(function (response) {
      return response.ok ? response.json() : null;
    })
    .then(function (data) {
      if (!data) return;
      if (Array.isArray(data.offers)) catalogue = data.offers;
      if (Array.isArray(data.claimed)) {
        claimed = data.claimed.map(function (item) { return item.id; });
        /* Kept only so the page can render before the next answer arrives.
         * The proxy is the authority; this is a cache, never a source. */
        try { localStorage.setItem(WALLET_KEY, JSON.stringify(claimed)); } catch (e) {}
      }
    })
    .catch(function () {})
    .then(decide, decide);
})();
